import { useCallback, useEffect, useMemo, useState } from "react";
import type { FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";

import { authClient } from "../lib/authClient";
import { OtpCodeInput } from "../components/auth/OtpCodeInput";
import {
  ReferralApiError,
  referralSignup,
  validateReferralCode,
} from "../lib/referralApi";
import type { StartReferralSignupResponse } from "../api/models";
import "./ReferralLandingPage.css";

type Step = "email" | "otp" | "success";

const EMPTY_OTP = ["", "", "", "", "", ""];

/** O OTP expira em 10 min e aceita 3 tentativas (regra do backend). */
const MAX_OTP_ATTEMPTS = 3;
const RESEND_COOLDOWN_SECONDS = 30;
/**
 * Fallback quando o `retry-after` não pode ser lido: o header ainda não está
 * em `exposeHeaders` do CORS, então em cross-origin o JS não vê o valor real.
 */
const DEFAULT_RETRY_AFTER_SECONDS = 60;

const STORAGE_KEY = "wedofi.referral.signup";

interface StoredSignup {
  code: string;
  email: string;
  step: Step;
}

/** Recupera o contexto do fluxo (e-mail/passo) se for do mesmo código. */
function readStoredSignup(code: string): StoredSignup | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<StoredSignup>;
    if (
      parsed?.code?.toUpperCase() !== code.toUpperCase() ||
      typeof parsed.email !== "string" ||
      (parsed.step !== "email" && parsed.step !== "otp" &&
        parsed.step !== "success")
    ) {
      return null;
    }

    return { code, email: parsed.email, step: parsed.step };
  } catch {
    return null;
  }
}

function writeStoredSignup(value: StoredSignup) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // localStorage indisponível (modo privado, cota) — segue sem persistir.
  }
}

export default function ReferralLandingPage() {
  const navigate = useNavigate();
  const { code: rawCode = "" } = useParams<{ code: string }>();
  const sessionQuery = authClient.useSession();

  /** O link funciona em qualquer caixa; a API normaliza para maiúsculas. */
  const urlCode = useMemo(() => rawCode.trim().toUpperCase(), [rawCode]);

  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [otp, setOtp] = useState<string[]>(EMPTY_OTP);
  const [otpError, setOtpError] = useState<string | null>(null);
  const [attemptsLeft, setAttemptsLeft] = useState(MAX_OTP_ATTEMPTS);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [blockedUntil, setBlockedUntil] = useState<number | null>(null);
  const [resendAvailableAt, setResendAvailableAt] = useState<number | null>(
    null,
  );
  const [tick, setTick] = useState(() => Date.now());

  // Restaura o contexto se a pessoa recarregou na tela de OTP.
  useEffect(() => {
    const stored = readStoredSignup(urlCode);
    if (!stored) return;

    setEmail(stored.email);
    setStep(stored.step);
  }, [urlCode]);

  // Ticker único para os contadores de espera (rate limit e reenvio).
  useEffect(() => {
    if (blockedUntil === null && resendAvailableAt === null) return;

    const timer = setInterval(() => setTick(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [blockedUntil, resendAvailableAt]);

  const secondsUntil = (target: number | null) =>
    target === null ? 0 : Math.max(0, Math.ceil((target - tick) / 1000));

  const blockedSeconds = secondsUntil(blockedUntil);
  const resendSeconds = secondsUntil(resendAvailableAt);

  const validation = useQuery({
    queryKey: ["referral-validate", urlCode],
    queryFn: ({ signal }) => validateReferralCode(urlCode, signal),
    enabled: urlCode.length > 0,
    retry: false,
    // O servidor cacheia por 10 min; uma validação por carregamento basta.
    staleTime: 10 * 60_000,
    refetchOnMount: false,
  });

  const validationError = validation.error instanceof ReferralApiError
    ? validation.error
    : null;
  const isInvalidCode = validationError?.isNotFound ?? false;
  const referralCode = validation.data?.code ?? urlCode;
  /**
   * Quem está convidando. A API só devolve o `label` do código (nome da
   * campanha/parceiro) — não há campo com o nome do usuário que indicou.
   */
  const inviterName = validation.data?.label ?? null;

  const blockFor = useCallback((seconds: number | null) => {
    const now = Date.now();
    setTick(now);
    setBlockedUntil(now + (seconds ?? DEFAULT_RETRY_AFTER_SECONDS) * 1000);
  }, []);

  const startResendCooldown = useCallback(() => {
    const now = Date.now();
    setTick(now);
    setResendAvailableAt(now + RESEND_COOLDOWN_SECONDS * 1000);
  }, []);

  const signup = useMutation<StartReferralSignupResponse, Error, string>({
    mutationFn: async (targetEmail) => {
      const result = await referralSignup({
        email: targetEmail,
        code: referralCode,
      });

      // `otpSent: false` = o e-mail já tem conta e esta rota não dispara OTP.
      // Em vez de deixar a pessoa sem código, pedimos o OTP de login direto ao
      // Better Auth: ela entra normalmente (o vínculo de indicação só vale
      // para contas novas).
      if (!result.otpSent) {
        const { error } = await authClient.emailOtp.sendVerificationOtp({
          email: targetEmail,
          type: "sign-in",
        });

        if (error) {
          throw new ReferralApiError({
            message: error.message ??
              "Este e-mail já tem conta. Entre pela tela de login.",
            statusCode: error.status ?? 0,
          });
        }
      }

      return result;
    },
    onSuccess: (_result, targetEmail) => {
      setStep("otp");
      setOtp(EMPTY_OTP);
      setOtpError(null);
      setFormError(null);
      setAttemptsLeft(MAX_OTP_ATTEMPTS);
      startResendCooldown();
      writeStoredSignup({ code: referralCode, email: targetEmail, step: "otp" });
    },
    onError: (error) => {
      if (!(error instanceof ReferralApiError)) {
        setFormError("Não foi possível enviar seu código. Tente novamente.");
        return;
      }

      if (error.isValidation) {
        setEmailError(
          error.fieldErrors.email ?? "Informe um e-mail válido.",
        );
        return;
      }

      if (error.isRateLimited) {
        blockFor(error.retryAfterSeconds);
        setFormError(error.message);
        return;
      }

      if (error.isNotFound) {
        setFormError(error.message);
        void validation.refetch();
        return;
      }

      setFormError(error.message);
    },
  });

  const handleSubmitEmail = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setEmailError(null);
    setFormError(null);

    const trimmed = email.trim();
    if (!trimmed) {
      setEmailError("Informe seu e-mail para continuar.");
      return;
    }

    setEmail(trimmed);
    signup.mutate(trimmed);
  };

  const handleVerifyOtp = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const otpCode = otp.join("");
    if (otpCode.length !== 6) {
      setOtpError("Digite os 6 dígitos do código.");
      return;
    }

    setOtpError(null);
    setIsVerifying(true);

    try {
      const { error } = await authClient.signIn.emailOtp({
        email,
        otp: otpCode,
      });

      if (error) {
        // Better Auth devolve o objeto cru, sem o envelope da API.
        if (error.code === "OTP_EXPIRED") {
          setOtpError("Código expirado. Peça um novo código.");
          setAttemptsLeft(0);
          return;
        }

        if (error.code === "TOO_MANY_ATTEMPTS") {
          setOtpError(
            "Tentativas esgotadas. Peça um novo código para continuar.",
          );
          setAttemptsLeft(0);
          return;
        }

        const remaining = Math.max(0, attemptsLeft - 1);
        setAttemptsLeft(remaining);
        setOtpError(
          remaining > 0
            ? `Código inválido. Você ainda tem ${remaining} ${
              remaining === 1 ? "tentativa" : "tentativas"
            }.`
            : "Tentativas esgotadas. Peça um novo código para continuar.",
        );
        setOtp(EMPTY_OTP);
        return;
      }

      // A conta é criada pelo backend na confirmação do OTP e o Better Auth já
      // abre a sessão. Como a landing entrega o app (e não a área logada),
      // encerramos a sessão e mostramos a tela de download.
      await authClient.signOut().catch((signOutError) => {
        console.warn("[referral] falha ao encerrar a sessão:", signOutError);
      });

      setStep("success");
      writeStoredSignup({ code: referralCode, email, step: "success" });
    } catch (error) {
      console.error("[referral] erro ao verificar OTP:", error);
      setOtpError("Erro ao verificar o código. Tente novamente.");
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResend = async () => {
    setOtpError(null);
    setFormError(null);
    setIsResending(true);

    try {
      // Reenvio pelo Better Auth não consome o rate limit de 5/h do
      // /referrals/signup; se ele falhar, refaz o signup (que mantém o vínculo).
      const { error } = await authClient.emailOtp.sendVerificationOtp({
        email,
        type: "sign-in",
      });

      if (error) {
        await signup.mutateAsync(email).catch(() => undefined);
        return;
      }

      setOtp(EMPTY_OTP);
      setAttemptsLeft(MAX_OTP_ATTEMPTS);
      startResendCooldown();
    } catch (error) {
      console.error("[referral] erro ao reenviar OTP:", error);
      setOtpError("Não foi possível reenviar o código. Tente novamente.");
    } finally {
      setIsResending(false);
    }
  };

  const handleChangeEmail = () => {
    setStep("email");
    setOtp(EMPTY_OTP);
    setOtpError(null);
    setFormError(null);
    writeStoredSignup({ code: referralCode, email, step: "email" });
  };

  const isSending = signup.isPending;
  const isBlocked = blockedSeconds > 0;

  if (validation.isPending) {
    return (
      <div className="rl-page">
        <div className="rl-card rl-card--loading" aria-busy="true">
          <div className="rl-skeleton rl-skeleton--badge" />
          <div className="rl-skeleton rl-skeleton--title" />
          <div className="rl-skeleton rl-skeleton--text" />
          <div className="rl-skeleton rl-skeleton--text rl-skeleton--short" />
          <div className="rl-skeleton rl-skeleton--field" />
          <div className="rl-skeleton rl-skeleton--button" />
          <span className="rl-loading-label">Validando seu convite...</span>
        </div>
      </div>
    );
  }

  if (isInvalidCode) {
    return (
      <div className="rl-page">
        <div className="rl-card rl-card--centered">
          <div className="rl-emoji" aria-hidden="true">🎟️</div>
          <h1 className="rl-title">Convite inválido ou expirado</h1>
          <p className="rl-lead">
            O código <strong>{urlCode || "—"}</strong>{" "}
            não está mais ativo. O acesso ao wedofi é por convite: peça um link
            novo a quem te indicou.
          </p>
          <button
            type="button"
            className="rl-cta"
            onClick={() => navigate("/login")}
          >
            Já tenho conta — entrar
          </button>
        </div>
      </div>
    );
  }

  if (validation.isError) {
    const isRateLimited = validationError?.isRateLimited ?? false;

    return (
      <div className="rl-page">
        <div className="rl-card rl-card--centered">
          <div className="rl-emoji" aria-hidden="true">⏳</div>
          <h1 className="rl-title">
            {isRateLimited ? "Muitas tentativas" : "Não conseguimos validar"}
          </h1>
          <p className="rl-lead">
            {validationError?.message ??
              "Tivemos um problema ao validar seu convite. Tente novamente em instantes."}
          </p>
          <button
            type="button"
            className="rl-cta"
            onClick={() => void validation.refetch()}
            disabled={validation.isFetching}
          >
            {validation.isFetching ? "Tentando..." : "Tentar novamente"}
          </button>
          <button
            type="button"
            className="rl-link-btn"
            onClick={() => navigate("/login")}
          >
            Ir para o cadastro normal
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="rl-page">
      <div className="rl-card">
        {sessionQuery.data?.session && step !== "success" && (
          <div className="rl-alert rl-alert--info">
            <span>
              Você já está conectado
              {sessionQuery.data.user?.email
                ? ` como ${sessionQuery.data.user.email}`
                : ""}.
            </span>
            <button
              type="button"
              className="rl-link-btn"
              onClick={() => navigate("/profile")}
            >
              Ir para o app
            </button>
          </div>
        )}

        {step === "email" && (
          <>
            <span className="rl-badge">🔒 Acesso somente por convite</span>

            <h1 className="rl-title">
              {inviterName
                ? `${inviterName} está te convidando para o wedofi`
                : "Você foi convidado para o wedofi"}
            </h1>

            <p className="rl-lead">
              O wedofi está liberando o acesso aos poucos, e este convite
              garante o seu lugar. Informe seu e-mail para entrar na lista e
              criar sua conta agora.
            </p>

            <ul className="rl-benefits">
              <li>
                <span aria-hidden="true">🎟️</span> Entrada direta pelo convite,
                sem fila de espera
              </li>
              <li>
                <span aria-hidden="true">⚡</span> Compre e venda cripto com PIX,
                sem burocracia
              </li>
              <li>
                <span aria-hidden="true">🤖</span> Agente de IA que acompanha seu
                portfólio
              </li>
            </ul>

            <form className="rl-form" onSubmit={handleSubmitEmail} noValidate>
              <label className="rl-field">
                <span className="rl-field-label">Seu e-mail</span>
                <input
                  type="email"
                  className={`rl-input ${emailError ? "rl-input--error" : ""}`}
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value);
                    setEmailError(null);
                  }}
                  placeholder="voce@email.com"
                  autoComplete="email"
                  name="email"
                  inputMode="email"
                  disabled={isSending || isBlocked}
                  aria-invalid={Boolean(emailError)}
                  aria-describedby={emailError ? "rl-email-error" : undefined}
                />
                {emailError && (
                  <span className="rl-field-error" id="rl-email-error">
                    {emailError}
                  </span>
                )}
              </label>

              {formError && !emailError && (
                <p className="rl-alert rl-alert--error">{formError}</p>
              )}

              {isBlocked && (
                <p className="rl-alert rl-alert--warn">
                  Aguarde {blockedSeconds}s para tentar de novo.
                </p>
              )}

              <button
                type="submit"
                className="rl-cta"
                disabled={isSending || isBlocked}
              >
                {isSending
                  ? "Enviando código..."
                  : isBlocked
                  ? `Aguarde ${blockedSeconds}s`
                  : "Garantir meu acesso"}
              </button>
            </form>

            <p className="rl-footnote">
              Enviamos um código de 6 dígitos para confirmar seu e-mail. Não é
              preciso criar senha.
            </p>
            <p className="rl-code-tag">
              Convite <strong>{referralCode}</strong>
            </p>
          </>
        )}

        {step === "otp" && (
          <>
            <span className="rl-badge">🎟️ Último passo</span>

            <h1 className="rl-title">Digite o código que enviamos</h1>

            <p className="rl-lead">
              Enviamos um código de 6 dígitos para{" "}
              <strong className="rl-email">{email}</strong>. Confirme para
              garantir seu acesso — ele vale por 10 minutos.
            </p>

            <form className="rl-form" onSubmit={handleVerifyOtp}>
              <OtpCodeInput
                value={otp}
                onChange={(digits) => {
                  setOtp(digits);
                  setOtpError(null);
                }}
                disabled={isVerifying || attemptsLeft === 0}
                autoFocus
              />

              {otpError && <p className="rl-alert rl-alert--error">{otpError}</p>}

              {formError && <p className="rl-alert rl-alert--error">{formError}</p>}

              {isBlocked && (
                <p className="rl-alert rl-alert--warn">
                  Muitas solicitações. Aguarde {blockedSeconds}s.
                </p>
              )}

              <button
                type="submit"
                className="rl-cta"
                disabled={isVerifying || otp.join("").length !== 6 ||
                  attemptsLeft === 0}
              >
                {isVerifying ? "Verificando..." : "Entrar no wedofi"}
              </button>
            </form>

            <div className="rl-actions">
              <button
                type="button"
                className="rl-link-btn"
                onClick={() => void handleResend()}
                disabled={isResending || isSending || resendSeconds > 0 ||
                  isBlocked}
              >
                {isResending
                  ? "Reenviando..."
                  : resendSeconds > 0
                  ? `Reenviar em ${resendSeconds}s`
                  : "Reenviar código"}
              </button>
              <button
                type="button"
                className="rl-link-btn"
                onClick={handleChangeEmail}
                disabled={isVerifying}
              >
                Alterar e-mail
              </button>
            </div>

            <p className="rl-footnote">
              São 3 tentativas por código. Depois disso, peça um novo.
            </p>
          </>
        )}

        {step === "success" && (
          <div className="rl-success">
            <div className="rl-emoji" aria-hidden="true">🎉</div>

            <h1 className="rl-title">Seu acesso está garantido</h1>

            <p className="rl-lead">
              Criamos sua conta com o e-mail{" "}
              <strong className="rl-email">{email}</strong>. Agora é só baixar o
              app do wedofi e entrar com esse mesmo e-mail — o código chega por
              lá na hora de acessar.
            </p>

            <div className="rl-stores">
              {/* Links definitivos entram quando o app for publicado nas lojas */}
              <a className="rl-storeBtn" href="#" role="button">
                <span className="rl-storeBtn-icon" aria-hidden="true">🤖</span>
                <span className="rl-storeBtn-text">
                  <span className="rl-storeBtn-label">Baixar para</span>
                  <strong>Android</strong>
                </span>
              </a>
              <a className="rl-storeBtn" href="#" role="button">
                <span className="rl-storeBtn-icon" aria-hidden="true">🍎</span>
                <span className="rl-storeBtn-text">
                  <span className="rl-storeBtn-label">Baixar para</span>
                  <strong>iOS</strong>
                </span>
              </a>
            </div>

            <p className="rl-footnote">
              Use sempre o mesmo e-mail deste convite: é ele que mantém sua
              indicação vinculada a quem te trouxe.
            </p>
            <p className="rl-code-tag">
              Convite <strong>{referralCode}</strong>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
